import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Car,
  Shield,
  Clock,
  Calendar,
  ThumbsUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const ReviewCard = ({ review }: { review: any }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobile = useIsMobile();

  const maxLength = isMobile ? 120 : 1000;
  const isLong = review.text.length > maxLength;
  const displayText =
    isExpanded || !isLong
      ? review.text
      : `${review.text.substring(0, maxLength)}...`;

  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl h-full flex flex-col relative overflow-hidden transition-all duration-300">
      <CardContent className="p-6 flex flex-col flex-grow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg shrink-0">
              {review.name.charAt(0)}
            </div>
            <div>
              <p className="font-bold text-slate-900 text-sm">{review.name}</p>
              <p className="text-slate-500 text-xs">{review.time}</p>
            </div>
          </div>
          <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
        </div>
        <div className="flex gap-1 mb-3">
          {[...Array(review.rating)].map((_, i) => (
            <Star key={i} className="w-4 h-4 text-[#FBBC04] fill-[#FBBC04]" />
          ))}
        </div>
        <div className="flex-grow flex flex-col">
          <p className="text-slate-700 text-sm leading-relaxed">
            "{displayText}"
          </p>
          {isLong && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-primary font-bold text-sm mt-2 flex items-center gap-1 self-start hover:underline"
            >
              {isExpanded ? "Lees minder" : "Lees meer"}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const Index = () => {
  const stepsRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: stepsRef,
    offset: ["start center", "end center"],
  });

  const isMobile = useIsMobile();
  const amplitude = isMobile ? 32 : 42;

  const pathD =
    `M 50 0 ` +
    Array.from({ length: 100 }, (_, i) => {
      const p = (i + 1) / 100;
      const x = 50 + Math.sin(p * 2.5 * 2 * Math.PI) * amplitude;
      const y = p * 100;
      return `L ${x} ${y}`;
    }).join(" ");

  const progressPoints = Array.from({ length: 101 }, (_, i) => i / 100);

  const carLeft = useTransform(
    scrollYProgress,
    progressPoints,
    progressPoints.map(
      (p) => `${50 + Math.sin(p * 2.5 * 2 * Math.PI) * amplitude}%`,
    ),
  );

  const carTop = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const carRotate = useTransform(
    scrollYProgress,
    progressPoints,
    progressPoints.map((p) => {
      const dx =
        Math.cos(p * 2.5 * 2 * Math.PI) * 2.5 * 2 * Math.PI * amplitude;
      const dy = 100;
      const angle = Math.atan2(dy, Math.abs(dx)) * (180 / Math.PI);
      return dx >= 0 ? angle : -angle;
    }),
  );

  const carScaleX = useTransform(
    scrollYProgress,
    progressPoints,
    progressPoints.map((p) => {
      const dx =
        Math.cos(p * 2.5 * 2 * Math.PI) * 2.5 * 2 * Math.PI * amplitude;
      return dx >= 0 ? 1 : -1;
    }),
  );

  const features = [
    {
      icon: <CheckCircle2 className="w-6 h-6 text-primary" />,
      title: "Proefles is vrijblijvend",
      description:
        "Maak geheel vrijblijvend kennis met onze autorijschool en ervaren rijinstructeur.",
    },
    {
      icon: <Star className="w-6 h-6 text-primary" />,
      title: "Hoog slagingspercentage",
      description:
        "Onze effectieve lesmethode zorgt ervoor dat jij snel je rijbewijs kunt halen.",
    },
    {
      icon: <Clock className="w-6 h-6 text-primary" />,
      title: "Géén wachttijd, start direct",
      description:
        "Je kunt bij ons direct beginnen met je eerste goedkope rijlessen in de buurt.",
    },
    {
      icon: <Calendar className="w-6 h-6 text-primary" />,
      title: "Zelf rijlessen plannen",
      description:
        "Plan eenvoudig je eigen autorijlessen in via onze handige Rijles App.",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Proefles",
      description:
        "We kijken naar je niveau, bespreken je wensen en je maakt kennis met de auto.",
    },
    {
      number: "2",
      title: "Pakket Kiezen",
      description:
        "Je krijgt een advies op maat, we kiezen het juiste pakket en plannen je lessen in.",
    },
    {
      number: "3",
      title: "Theorie Leren",
      description:
        "Start direct met je theorie. Wij helpen je met het juiste studiemateriaal.",
    },
    {
      number: "4",
      title: "Rijlessen",
      description:
        "Stap voor stap bouw je zelfvertrouwen op achter het stuur met je vaste instructeur.",
    },
    {
      number: "5",
      title: "Rijbewijs Halen",
      description:
        "Je gaat vol zelfvertrouwen naar het CBR examen en haalt veilig je rijbewijs!",
    },
  ];

  const reviews = [
    {
      name: "Dave Galewicz",
      text: "In 1 keer geslaagd met behulp van Rijschool de diamant fijne lessen en top instructeurs",
      rating: 5,
      time: "3 weken geleden",
    },
    {
      name: "Ivo Van es",
      text: "Hele fijne rijschool! Je word gelijk geholpen en met een proefles kun je kijken of het wat voor jou is. Verder krijg je een eerlijk advies over de hoeveelheid lessen die je kunt nemen en proberen ze je ook echt zo snel, makkelijk en goedkoop mogelijk te laten slagen. Heel flexibel met lessen inplannen en reageert direct als je wat nodig hebt op whatsapp. Geen moeite en gezeik met betalingen of inplannen en een hele gezellige sfeer in de auto. Altijd goeie gesprekken en tegelijk leer je enorm veel over autorijden en de regels op de weg. Echt een aanrader!",
      rating: 5,
      time: "5 weken geleden",
    },
    {
      name: "Angelo Pira",
      text: "Ontzettend tevreden over de rijlessen! Ik ben in 2 keer geslaagd en dat komt zeker door de goede begeleiding van mijn rijinstructeur Souf. Tijdens de lessen werd altijd duidelijk uitgelegd wat beter kon en waar ik extra op moest letten. Ik had zelf veel moeite met besluitvaardigheid en doorstroming in het verkeer, maar dankzij de directe en effectieve aanpak van Souf heb ik hier vooruitgang in kunnen maken. Uiteindelijk is het daardoor gelukt. Zeker een aanrader!",
      rating: 5,
      time: "6 weken geleden",
    },
    {
      name: "Charissa A-tjak",
      text: "Na 6 x gezakt bij een andere rijschool, keuze gemaakt om naar rijschool diamand te gaan. En les gekregen van rij instructeur nazim. Hele goeie ervaring goed geholpen en daardoor eindelijk geslaagt voor me rijbewijs.",
      rating: 5,
      time: "20 weken geleden",
    },
    {
      name: "Ehsan Abbaspur",
      text: "Geslaagd bij instructeur souf, met zijn hulp heb ik binnen een korte tijd en beperkt aantal rijlessen mijn rijbewijs gehaald. Love!",
      rating: 5,
      time: "20 weken geleden",
    },
    {
      name: "itzborizz",
      text: "Goede rijschool. Word gewoon meteen gezegt wat je goed en fout doet geen slap verhaal er omheen. Goede lessen en daardoor in 1x geslaagd zeker aanrader.",
      rating: 5,
      time: "3 maanden geleden",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Rijschool Alphen aan den Rijn | De Diamant | Nummer 1 Autorijschool
        </title>
        <meta
          name="description"
          content="Zoek je een rijschool in Alphen aan den Rijn? Bij Rijschool De Diamant haal je snel en veilig je rijbewijs. Boek nu je proefles en start direct!"
        />
        <meta
          property="og:title"
          content="Rijschool Alphen aan den Rijn | De Diamant | Nummer 1 Autorijschool"
        />
        <meta
          property="og:description"
          content="Zoek je een rijschool in Alphen aan den Rijn? Bij Rijschool De Diamant haal je snel en veilig je rijbewijs. Boek nu je proefles en start direct!"
        />
        <meta property="og:url" content="https://rijschooldediamant.nl/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Rijschool Alphen aan den Rijn | De Diamant | Nummer 1 Autorijschool"
        />
        <meta
          name="twitter:description"
          content="Zoek je een rijschool in Alphen aan den Rijn? Bij Rijschool De Diamant haal je snel en veilig je rijbewijs."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[100svh] md:min-h-0 pt-[100px] pb-6 md:pt-20 md:pb-12 lg:pt-36 lg:pb-32 overflow-hidden bg-slate-900 flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://vibe.filesafe.space/1781862609113466713/attachments/aaf7240c-7b1e-440c-93ef-19cbe71eb5f6.jpg"
            alt="Hero"
            width="1920"
            height="1080"
            className="w-full h-full object-cover object-[80%_center] opacity-70"
            fetchPriority="high"
            decoding="async"
          />
        </div>
        {/* Gradient to blend with background and ensure text readability */}
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-slate-900/95 via-slate-900/60 to-transparent pointer-events-none" />
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900 pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10 w-full">
          <div className="flex flex-col lg:flex-row items-center gap-5 sm:gap-8 md:gap-12 lg:gap-20">
            <div className="w-full lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="hidden md:inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary mb-6 font-bold text-sm drawn-border">
                  <Shield className="w-4 h-4" />
                  Dé rijschool in Alphen aan den Rijn
                </div>
                <h1 className="text-xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-2 md:mb-4">
                  Rijschool Alphen aan den Rijn
                </h1>
                <p className="text-base sm:text-2xl md:text-3xl lg:text-4xl font-bold text-primary relative inline-block mb-10 md:mb-8">
                  Direct starten? Meld je aan!
                  <svg
                    className="absolute -bottom-1 md:-bottom-2 left-0 w-full h-2 md:h-3 text-primary/30"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 5 Q 50 10 100 5"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                  </svg>
                </p>

                <ul className="space-y-1.5 md:space-y-3 mb-12 text-slate-200 font-medium text-[12px] sm:text-base md:text-lg overflow-hidden">
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="flex items-center gap-2.5 md:gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-primary shrink-0" />
                    <span>Proefles is gratis bij pakket</span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="flex items-center gap-2.5 md:gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-primary shrink-0" />
                    <span>Hoog slagingspercentage</span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="flex items-center gap-2.5 md:gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-primary shrink-0" />
                    <span>Géén wachttijd, start direct</span>
                  </motion.li>
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                    className="flex items-center gap-2.5 md:gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-primary shrink-0" />
                    <span>Zelf rijlessen plannen met Rijles App</span>
                  </motion.li>
                </ul>

                <div className="flex flex-col sm:flex-row gap-2.5 md:gap-4 mb-5 md:mb-8">
                  <Button
                    asChild
                    size="lg"
                    className="drawn-border drawn-shadow text-[12px] sm:text-[15px] md:text-lg h-10 sm:h-12 md:h-14 px-4 sm:px-6 md:px-8 hover-wiggle"
                  >
                    <Link to="/proefles">Proefles Aanvragen</Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="drawn-border text-[12px] sm:text-[15px] md:text-lg h-10 sm:h-12 md:h-14 px-4 sm:px-6 md:px-8 bg-white text-slate-900"
                  >
                    <Link to="/rijlespakketten">Bekijk Tarieven</Link>
                  </Button>
                </div>

                <div className="flex justify-center md:justify-start w-full mb-10 md:mb-0">
                  <div className="flex items-center justify-center gap-1 sm:gap-2 md:gap-4 text-[9px] sm:text-[11px] md:text-sm text-slate-200 bg-slate-900/60 backdrop-blur-md p-1.5 sm:p-2 md:p-3 rounded-xl w-fit border border-primary/30 shadow-lg shadow-primary/10 drawn-border max-w-[calc(100vw-160px)] sm:max-w-none">
                    <div className="flex -space-x-1.5 md:-space-x-2 shrink-0">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          className="w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-full bg-slate-800 border-[1.5px] md:border-2 border-slate-900 flex items-center justify-center text-xs font-bold"
                        >
                          <Star className="w-2 h-2 md:w-3 md:h-3 text-yellow-400 fill-yellow-400" />
                        </div>
                      ))}
                    </div>
                    <p className="whitespace-nowrap text-ellipsis overflow-hidden">
                      Meer dan{" "}
                      <strong className="text-primary font-bold text-[10px] sm:text-[13px] md:text-base">
                        500+
                      </strong>{" "}
                      geslaagde leerlingen
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="w-full lg:w-1/2 flex justify-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative w-full max-w-[400px]"
              >
                <div className="w-full shadow-2xl drawn-border-messy drawn-shadow bg-white relative">
                  <div className="overflow-hidden rounded-xl bg-white h-[clamp(280px,40svh,385px)] md:h-[385px] relative">
                    <iframe
                      src="https://startmetjerijbewijs.nl/inschrijven/rijschool-de-diamant"
                      className="absolute top-[-25px] left-[-15px] w-[calc(100%+30px)] h-[600px] border-0"
                      title="Inschrijfformulier Rijschool De Diamant"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-4 mt-4 pb-4 text-slate-600 font-medium text-sm">
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-4 h-4 text-primary" />
                      <span>Erkend</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      <span>5.0/5.0 Reviews</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 bg-slate-50 bg-drawn-pattern relative overflow-hidden">
        {/* Golden Beam */}
        <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-yellow-400/15 rounded-full blur-[100px] pointer-events-none z-0 animate-breathe" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-amber-500/15 rounded-full blur-[120px] pointer-events-none z-0 animate-breathe-slow" />

        <img
          loading="lazy"
          decoding="async"
          src="https://vibe.filesafe.space/1781862609113466713/attachments/8fba4f0d-f2c8-44d7-8b51-209b721de0fd.png"
          alt="Lesauto Rijschool De Diamant rijdend in Alphen aan den Rijn - Decoratief"
          width="200"
          height="200"
          className="absolute top-8 left-[2%] md:left-[10%] w-32 md:w-48 opacity-[0.15] float-slow rotate-[-15deg] pointer-events-none z-0"
        />
        <img
          loading="lazy"
          decoding="async"
          src="https://vibe.filesafe.space/1781862609113466713/attachments/8fba4f0d-f2c8-44d7-8b51-209b721de0fd.png"
          alt="Rijles in Alphen aan den Rijn met Rijschool De Diamant - Decoratief"
          width="200"
          height="200"
          className="absolute bottom-8 right-[2%] md:right-[10%] w-40 md:w-56 opacity-[0.12] float-medium rotate-[25deg] pointer-events-none z-0"
        />
        <img
          loading="lazy"
          decoding="async"
          src="https://vibe.filesafe.space/1781862609113466713/attachments/8fba4f0d-f2c8-44d7-8b51-209b721de0fd.png"
          alt="Auto theorie en praktijk Alphen aan den Rijn - Decoratief"
          width="200"
          height="200"
          className="absolute top-1/3 left-[40%] w-24 md:w-32 opacity-[0.1] float-fast rotate-[10deg] pointer-events-none z-0"
        />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Waarom kiezen voor Rijschool De Diamant?
            </h2>
            <p className="text-base md:text-lg text-slate-600">
              Wij bieden jou de beste begeleiding op weg naar je rijbewijs. Met
              onze unieke lesmethode en persoonlijke aanpak in{" "}
              <Link
                to="/rijschool-alphen-aan-den-rijn"
                className="text-primary font-bold hover:underline"
              >
                Alphen aan den Rijn
              </Link>{" "}
              en omstreken slaag je sneller.
            </p>
          </div>

          <div className="flex overflow-x-auto pb-8 -mx-4 px-4 scroll-pl-4 snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 no-scrollbar">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="min-w-[70vw] sm:min-w-0 snap-start"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-none shadow-md hover:shadow-lg transition-shadow bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-6">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stappenplan Section */}
      <section className="py-12 md:py-24 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiMzOGJkZjgiIGZpbGwtb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] opacity-50 z-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16 max-w-5xl mx-auto mb-16">
            <div className="w-full md:w-1/2 md:-ml-24 lg:-ml-48">
              <motion.div
                initial={{ opacity: 0, x: -20, rotate: -4 }}
                whileInView={{ opacity: 1, x: 0, rotate: -2 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative w-full max-w-[300px] mx-auto md:ml-0 md:mr-auto aspect-[3/4] drawn-border-messy drawn-shadow bg-white overflow-hidden rounded-xl"
              >
                <img
                  src="https://vibe.filesafe.space/1781862609113466713/attachments/8e5f8713-737d-4028-980c-70a7def82e3d.jpg"
                  alt="Geslaagde leerling Rijschool De Diamant"
                  width="400"
                  height="533"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-center"
                />
              </motion.div>
            </div>
            <div className="w-full md:w-[55%] text-center md:text-left md:-ml-10 lg:-ml-20">
              <div className="inline-flex items-center justify-center p-3 bg-white rounded-full mb-4 shadow-sm drawn-border">
                <Car className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
                In 5 stappen naar je rijbewijs
              </h2>
              <p className="text-base md:text-lg text-slate-600">
                Onze werkwijze is simpel, transparant en gericht op jouw succes.
              </p>
            </div>
          </div>

          <div
            className="relative w-full max-w-5xl mx-auto h-[1200px] md:h-[1300px] mt-10"
            ref={stepsRef}
          >
            {/* SVG Road - Two Lanes */}
            <svg
              className="absolute top-0 left-0 w-full h-full z-0 overflow-visible"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              {/* Asphalt Base */}
              <path
                d={pathD}
                fill="none"
                stroke="#475569"
                strokeWidth={isMobile ? 30 : 40}
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Center Dashed Line */}
              <path
                d={pathD}
                fill="none"
                stroke="#ffffff"
                strokeWidth={isMobile ? 2 : 4}
                strokeDasharray={isMobile ? "10 10" : "15 15"}
                vectorEffect="non-scaling-stroke"
              />
              {/* Progress Overlay */}
              <motion.path
                d={pathD}
                fill="none"
                stroke="#38bdf8"
                strokeWidth={isMobile ? 30 : 40}
                strokeOpacity="0.4"
                vectorEffect="non-scaling-stroke"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ pathLength: scrollYProgress }}
              />
            </svg>

            {/* The Car */}
            <motion.div
              className="absolute z-30 -ml-6 -mt-6"
              style={{
                left: carLeft,
                top: carTop,
                rotate: carRotate,
                scaleX: carScaleX,
              }}
            >
              <div className="bg-white p-2 rounded-full shadow-lg border-2 border-primary text-primary transform hover:scale-110 transition-transform">
                <Car className="w-8 h-8" />
              </div>
            </motion.div>

            {/* The Steps */}
            {steps.map((step, index) => {
              const isRightPeak = index % 2 === 0;
              return (
                <div
                  key={index}
                  className={`absolute w-[65%] sm:w-[45%] md:w-[35%] transform -translate-y-1/2 ${isRightPeak ? "left-[2%] md:left-[8%]" : "right-[2%] md:right-[8%]"} z-10`}
                  style={{ top: `${index * 20 + 10}%` }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.5 }}
                  >
                    <Card className="bg-white border-2 border-slate-100 shadow-xl drawn-border overflow-hidden">
                      <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 bg-primary/10 text-primary flex items-center justify-center rounded-xl font-bold text-xl sm:text-2xl drawn-border">
                          {step.number}
                        </div>
                        <div>
                          <h3 className="text-base sm:text-xl font-bold text-slate-900 mb-1 sm:mb-2">
                            {step.title}
                          </h3>
                          <p className="text-slate-800 font-medium text-xs sm:text-sm md:text-base leading-snug">
                            {step.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-16">
            <Button
              asChild
              size="lg"
              className="drawn-border drawn-shadow text-lg h-14 px-8 hover-wiggle"
            >
              <Link to="/proefles">Plan stap 1: Proefles</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-12 md:py-20 bg-primary/5 relative overflow-hidden">
        {/* Golden Beam */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-yellow-500/10 rounded-full blur-[120px] pointer-events-none z-0 animate-breathe" />

        <img
          loading="lazy"
          decoding="async"
          src="https://vibe.filesafe.space/1781862609113466713/attachments/8fba4f0d-f2c8-44d7-8b51-209b721de0fd.png"
          alt="Rijschool De Diamant Alphen aan den Rijn auto - Decoratief"
          width="200"
          height="200"
          className="absolute top-10 left-[2%] md:left-[5%] w-32 md:w-48 opacity-[0.3] float-medium rotate-[-10deg] pointer-events-none z-0"
        />
        <img
          loading="lazy"
          decoding="async"
          src="https://vibe.filesafe.space/1781862609113466713/attachments/8fba4f0d-f2c8-44d7-8b51-209b721de0fd.png"
          alt="Autorijles Alphen aan den Rijn - Decoratief"
          width="200"
          height="200"
          className="absolute bottom-10 right-[2%] md:right-[5%] w-40 md:w-56 opacity-[0.25] float-slow rotate-[20deg] pointer-events-none z-0"
        />
        <img
          loading="lazy"
          decoding="async"
          src="https://vibe.filesafe.space/1781862609113466713/attachments/8fba4f0d-f2c8-44d7-8b51-209b721de0fd.png"
          alt="Beste rijschool Alphen aan den Rijn - Decoratief"
          width="200"
          height="200"
          className="absolute bottom-10 left-[2%] md:left-[5%] w-32 md:w-48 opacity-[0.3] float-fast rotate-[5deg] pointer-events-none z-0"
        />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <div className="inline-flex items-center justify-center p-3 bg-white rounded-full mb-4 shadow-sm">
              <ThumbsUp className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">
              Wat onze leerlingen zeggen
            </h2>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className="w-6 h-6 text-[#FBBC04] fill-[#FBBC04]"
                  />
                ))}
              </div>
              <span className="text-xl font-bold text-slate-900">5.0</span>
            </div>
            <p className="text-lg text-slate-600 mb-2">
              Gebaseerd op Google Reviews
            </p>
          </div>
          <div className="flex overflow-x-auto pb-8 -mx-4 px-4 scroll-pl-4 snap-x snap-mandatory md:grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto no-scrollbar">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                className="min-w-[70vw] sm:min-w-[300px] snap-start md:min-w-0"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ReviewCard review={review} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Cities Section */}
      <section className="py-12 md:py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Rijschool in jouw regio
            </h2>
            <p className="text-slate-600">
              Wij zijn actief in de wijde omtrek van Alphen aan den Rijn. Klik
              op jouw woonplaats voor meer lokale informatie.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { name: "Alphen aan den Rijn", slug: "alphen-aan-den-rijn" },
              { name: "Ter Aar", slug: "ter-aar" },
              { name: "Boskoop", slug: "boskoop" },
              { name: "Nieuwkoop", slug: "nieuwkoop" },
              { name: "Aarlanderveen", slug: "aarlanderveen" },
              { name: "Zevenhoven", slug: "zevenhoven" },
              { name: "Hazerswoude-Rijndijk", slug: "hazerswoude-rijndijk" },
              { name: "Hazerswoude-Dorp", slug: "hazerswoude-dorp" },
              { name: "Woubrugge", slug: "woubrugge" },
              { name: "Langeraar", slug: "langeraar" },
              {
                name: "Koudekerk aan den Rijn",
                slug: "koudekerk-aan-den-rijn",
              },
              { name: "Zwammerdam", slug: "zwammerdam" },
            ].map((city) => (
              <Link
                key={city.slug}
                to={`/rijschool-${city.slug}`}
                className="p-3 text-center rounded-xl bg-slate-50 hover:bg-primary/10 text-slate-700 hover:text-primary font-medium transition-colors drawn-border text-sm md:text-base"
              >
                {city.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
